import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined'
import Groups2OutlinedIcon from '@mui/icons-material/Groups2Outlined'
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined'
import PersonPinOutlinedIcon from '@mui/icons-material/PersonPinOutlined'
import {
  Avatar,
  Badge,
  Button,
  Card,
  Container,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography
} from '@mui/material'
import { CompanyUsersStatus } from '@shared/types/companies.types'
import { Permission } from '@shared/types/roles.types'
import { t } from 'i18next'
import { useMemo } from 'react'
import {
  Link,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
  useParams
} from 'react-router'
import CompanyLayoutLoader from '../components/loaders/CompanyLayoutLoader'
import { useAuthStore } from '../hooks/useAuthStore'
import {
  useCompany,
  useCompanyUser,
  useCompanyUsers
} from '../hooks/useQueries/useCompanies'
import { useUser } from '../hooks/useQueries/useUsers'
import { getAvatarCompanyName } from '../utils/company.utils'

const tabStyles = {
  textTransform: 'none',
  padding: '0.25rem 1rem',
  borderRadius: '0.5rem',
  minHeight: '2.625rem',
  '&.active': {
    color: 'primary.main',
    backgroundColor: 'rgba(159, 168, 218, 0.22)'
  }
}

export default function CompanyLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const { userId } = useAuthStore()
  const params = useParams()
  const companyId = params.companyId!
  const { data: company, isLoading } = useCompany(companyId!)
  const { data: user, isLoading: isLoadingUser } = useUser(userId!)
  const { data: userCompany, isLoading: isLoadingUserCompany } = useCompanyUser(
    companyId,
    userId!
  )

  const { data: receivedInvitations, isLoading: isLoadingReceivedInvitations } =
    useCompanyUsers(companyId, {
      status: CompanyUsersStatus.PENDING_COMPANY
    })
  const { data: sentInvitations, isLoading: isLoadingSentInvitations } =
    useCompanyUsers(companyId, {
      status: CompanyUsersStatus.PENDING_USER
    })

  const { hasUpdatePermission } = useMemo(() => {
    const permissions = userCompany?.permissions ?? []
    const fullAccess = userCompany?.role.fullAccess ?? false

    return {
      hasUpdatePermission:
        permissions.includes(Permission.COMPANIES_UPDATE) || fullAccess,

      hasInvitationsPermission:
        permissions.includes(Permission.USERS_ACCEPT) || fullAccess,

      hasMembersPermission: true
    }
  }, [userCompany])

  if (
    isLoading ||
    isLoadingUser ||
    isLoadingUserCompany ||
    isLoadingReceivedInvitations ||
    isLoadingSentInvitations ||
    !company ||
    !user
  ) {
    return <CompanyLayoutLoader />
  }

  if (location.pathname === `/companies/${companyId}`) {
    navigate(`/companies/${companyId}/workspaces`)
  }

  const isInvitationsTabActive = location.pathname.startsWith(
    `/companies/${companyId}/invitations`
  )

  return (
    <>
      <Container maxWidth='xl' disableGutters>
        <Card
          elevation={0}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            padding: '1.5rem',
            backgroundColor: 'background.default',
            width: '100%',
            justifyContent: 'space-between'
          }}
        >
          <Stack direction='row' alignItems='center' gap='1rem'>
            {company?.logoUri ? (
              <Avatar
                src={company.logoUri}
                variant='square'
                sx={{
                  width: '4rem',
                  height: '4rem',
                  borderRadius: '.25rem',
                  backgroundColor: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '& img': {
                    objectFit: 'contain',
                    width: '100%',
                    height: '100%'
                  }
                }}
              />
            ) : (
              <Avatar
                variant='square'
                sx={{
                  width: '4rem',
                  height: '4rem',
                  borderRadius: '.25rem'
                }}
              >
                {getAvatarCompanyName(company.name!)}
              </Avatar>
            )}
            <Stack direction='column' gap='0.25rem'>
              <Typography variant='h6' fontWeight='bold'>
                {company!.name}
              </Typography>
              {hasUpdatePermission && (
                <Button
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    textAlign: 'left',
                    justifyContent: 'flex-start',
                    padding: '0.25rem 0'
                  }}
                  color='primary'
                  size='small'
                  component={Link}
                  to={`/companies/${companyId}/profile`}
                >
                  {t('companies.layout.edit_company')}
                </Button>
              )}
            </Stack>
          </Stack>

          {hasUpdatePermission && (
            <Stack direction='row' spacing='1.5rem'>
              <Stack direction='row' spacing='.5rem'>
                <Typography
                  variant='body2'
                  color='white'
                  sx={{ marginBottom: '0.25rem' }}
                >
                  {t('common.nif')}:
                </Typography>
                <Typography variant='body2' fontWeight='bold' color='white'>
                  {company!.tin}
                </Typography>
              </Stack>
              <Stack direction='row' spacing='.5rem'>
                <Typography
                  variant='body2'
                  color='white'
                  sx={{ marginBottom: '0.25rem' }}
                >
                  {t('common.address')}:
                </Typography>
                {company!.address ? (
                  <Typography variant='body2' fontWeight='bold' color='white'>
                    {company!.address}
                  </Typography>
                ) : (
                  <Typography variant='body2' color='text.disabled'>
                    {t('common.empty_field')}
                  </Typography>
                )}
              </Stack>
              <Stack direction='row' spacing='.5rem'>
                <Typography
                  variant='body2'
                  color='white'
                  sx={{ marginBottom: '0.25rem' }}
                >
                  {t('common.url')}:
                </Typography>
                {company!.webUrl ? (
                  company!.webUrl.startsWith('http://') ||
                  company!.webUrl.startsWith('https://') ? (
                    <Typography
                      variant='body2'
                      fontWeight='bold'
                      color='white'
                      component='a'
                      href={company!.webUrl}
                      target='_blank'
                      rel='noopener noreferrer'
                      sx={{
                        textDecoration: 'none',
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      {company!.webUrl}
                    </Typography>
                  ) : (
                    <Typography variant='body2' fontWeight='bold' color='white'>
                      {company!.webUrl}
                    </Typography>
                  )
                ) : (
                  <Typography variant='body2' color='text.disabled'>
                    {t('common.empty_field')}
                  </Typography>
                )}
              </Stack>
            </Stack>
          )}
        </Card>

        <Tabs value={false} sx={{ marginBottom: '1rem' }}>
          <Tab
            component={NavLink}
            to={`/companies/${companyId}/workspaces`}
            icon={<FolderOpenOutlinedIcon />}
            iconPosition='start'
            label={t('companies.layout.workspaces')}
            sx={tabStyles}
          />

          <Tab
            component={NavLink}
            to={`/companies/${companyId}/members`}
            icon={<Groups2OutlinedIcon />}
            iconPosition='start'
            label={t('companies.layout.members')}
            sx={tabStyles}
          />

          <Tab
            component={NavLink}
            to={`/companies/${companyId}/roles`}
            icon={<PersonPinOutlinedIcon />}
            iconPosition='start'
            label={t('companies.layout.roles')}
            sx={tabStyles}
          />

          <Tab
            component={NavLink}
            to={`/companies/${companyId}/invitations/sent`}
            icon={<MailOutlineOutlinedIcon />}
            iconPosition='start'
            label={
              <Stack direction='row' alignItems='center' gap='1rem'>
                <span>{t('account.invitations')}</span>
                <Badge
                  color='primary'
                  badgeContent={
                    (receivedInvitations?.length || 0) +
                    (sentInvitations?.length || 0)
                  }
                  sx={{
                    '& .MuiBadge-badge': {
                      backgroundColor: '#9FA8DA',
                      color: 'rgba(0, 0, 0, 0.87)'
                    }
                  }}
                />
              </Stack>
            }
            className={isInvitationsTabActive ? 'active' : ''}
            sx={tabStyles}
          />
        </Tabs>

        <Paper
          sx={{
            padding: '1.5rem 3rem 2rem 3rem',
            minHeight: '42.625rem',
            borderRadius: '1rem',
            border: '1px solid #00897B',
            background:
              'linear-gradient(156deg, #1B5A4B -21.63%, #171B1E 42.81%), #171B1E',
            boxShadow:
              '0 4px 18px 3px rgba(0, 0, 0, 0.12), 0 10px 14px 1px rgba(0, 0, 0, 0.14), 0 6px 6px -3px rgba(0, 0, 0, 0.20)'
          }}
        >
          <Outlet />
        </Paper>
      </Container>
    </>
  )
}
